/**
 * Client for the local Nova engine.
 *
 * The engine is a Python sidecar on loopback. When it is not running the shell
 * still works against seed data, clearly labelled as such -- a UI that shows an
 * empty grid because a background process died is indistinguishable from a UI
 * showing an empty library, and the difference matters a great deal.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { BeatGridFound, BeatGridState, CueField, CueState, Ending, Track } from "./types";
import { SEED_TRACKS } from "./seed";
import { browserEngineUrl, engineInfo } from "./platform";
import { withToken } from "./core/session";

/** Resolved once at startup; the desktop shell reports the port it bound. */
let engineUrl = browserEngineUrl();

/** Rows per request. Matches the engine's default page size. */
export const PAGE_SIZE = 200;

export type EngineState = "connecting" | "connected" | "offline";

/**
 * What the library watch is doing.
 *
 * Three separate booleans on purpose. `available` is whether the engine has a
 * filesystem watcher at all; `enabled` is whether the curator wants one;
 * `watching` is whether one is actually installed right now. They come apart --
 * an unplugged drive leaves a root swept but not watched -- and collapsing them
 * into "on" would hide the only two states worth acting on.
 */
export interface WatchStatus {
  available: boolean;
  unavailableReason: string | null;
  enabled: boolean;
  running: boolean;
  watching: boolean;
  roots: { id: number; path: string }[];
  sweepMinutes: number;
  pending: number;
  settleSeconds: number;
  error: string | null;
  stats: {
    events: number;
    settled: number;
    added: number;
    updated: number;
    reattached: number;
    missing: number;
    unchanged: number;
    placeholders: number;
    errors: number;
    sweeps: number;
    overflows: number;
    lastEventAt: number | null;
    lastChangeAt: number | null;
    lastSweepAt: number | null;
    lastSweepSummary: string | null;
  };
}

/** One song in a playlist. */
export interface PlaylistItem {
  id: number;
  type: "song" | "advert" | "breakNote" | "voiceTrack" | "marker" | "ident";
  position: number;
  trackId: string | null;
  title: string | null;
  artist: string | null;
  duration: number | null;
  bpm: number | null;
  camelot: string | null;
  keyName: string | null;
  locked: boolean;
  params: Record<string, unknown>;
  /** The file is gone or archived. Kept in the run, flagged, never dropped. */
  missing: boolean;
  /** How this item follows the one before it. Null for the first, and after a break. */
  transition: {
    keyRelation: "same" | "adjacent" | "relative" | "lift" | "clash" | "unknown";
    bpmDrift: number | null;
    tempoRelation: "smooth" | "stretch" | "jolt" | "unknown";
    harmonic: boolean;
  } | null;
}

export interface Playlist {
  id: number;
  name: string;
  targetSeconds: number | null;
  items: PlaylistItem[];
  totalSeconds: number;
  /** Signed: negative is short of the target, positive is over it. */
  overSeconds: number | null;
  /** Songs with no measured length — counted, not silently treated as zero. */
  unmeasured: number;
  /** The clip rules play between its songs (off unless its maker turns it on). */
  clipRules?: boolean;
}

/** What songs can be sequenced on, in priority order. */
export const CRITERIA: { id: string; label: string; pending?: boolean }[] = [
  { id: "key", label: "Key" },
  { id: "tempo", label: "Tempo" },
  // The head exists and nothing fills it yet, so it is offered and labelled
  // rather than hidden — a criterion that silently does nothing is worse.
  { id: "energy", label: "Energy", pending: true },
  { id: "artistSpread", label: "Artist spread" },
];


export interface PlaylistSummary {
  id: number;
  name: string;
  targetSeconds: number | null;
  items: number;
}

export interface EngineStatus {
  tracks: number;
  genres: number;
  byState: Record<string, number>;
  scan: { running: boolean; summary: string | null; root: string | null };
  /** Absent when talking to an engine older than the watch. */
  watch?: WatchStatus;
  /** Tracks with deterministic measurements stored (LUFS et al). */
  measured: number;
  jobs: {
    workers: number;
    active: number;
    queued: number;
    running: number;
    failed: number;
    completed: number;
  };
}

export interface TracksPage {
  tracks: Track[];
  total: number;
  /** Every matching song's length in seconds, not only this page's. */
  totalSeconds?: number;
  /** Rows excluded only because a filtered field has not been computed yet. */
  pendingExcluded?: number;
  limit: number;
  offset: number;
  order: string;
  hasMore: boolean;
}

export interface CustomColumn {
  id: number;
  key: string;
  label: string;
  type: "text" | "number" | "boolean" | "date";
  position: number;
}

/**
 * Separator settings, in the engine's own shape.
 *
 * The engine describes itself: `options` lists every separator it supports for
 * each key, with the literal token it stands for, and `protected` says which are
 * never configurable and why. The dialog renders from that rather than from a
 * hardcoded list, so the UI cannot drift from what the engine actually honours.
 */
export interface SeparatorOption {
  name: string;
  token: string;
}

export interface SettingsPayload {
  /** Setting key -> enabled separator names, e.g. "split.genre.separators". */
  settings: Record<string, string[]>;
  defaults: Record<string, string[]>;
  options: Record<string, { available: SeparatorOption[]; default: string[] }>;
  protected: { name: string; token: string; reason: string }[];
}

export interface TreeCounts {
  tracks: number;
  missing: number;
  archived: number;
  total: number;
}

export interface TreeNode {
  kind: "root" | "folder";
  /** Roots only. */
  id?: number;
  name: string;
  /** Root-relative folder path; the string the archive endpoint wants. */
  path?: string;
  rootId?: number;
  removed?: boolean;
  removedAt?: string | null;
  /** Its songs only play when a person picks them (marked on this folder). */
  manualOnly?: boolean;
  /** A folder above it is marked, so its songs are hand-picked only too. */
  manualInherited?: boolean;
  /** This node plus everything under it. */
  counts: TreeCounts;
  /** Files sitting directly in this node. */
  directCounts: TreeCounts;
  children: TreeNode[];
  /** Its folders were left out of this answer: ask for them (`treeFolder`) when it opens. */
  more?: boolean;
  /** How many folders it has, when only some were sent. */
  childTotal?: number;
}

/** How many folders a tree answer sends per folder; "Show more" asks for the next ones. */
export const TREE_CHILDREN = 500;

/** One folder with its own folders (one level), from `offset`: for a big library's tree. */
export async function treeFolder(rootId: number, path: string, offset = 0): Promise<TreeNode | null> {
  try {
    const q = `rootId=${rootId}&path=${encodeURIComponent(path)}&depth=1&children=${TREE_CHILDREN}&offset=${offset}`;
    return (await call<{ folder: TreeNode | null }>(`/api/tree?${q}`)).folder;
  } catch {
    return null;
  }
}

export interface GenreFacet {
  name: string;
  tracks: number;
  /** Taxonomy path this spelling resolves to, or null if unrecognised. */
  resolved: string | null;
}

export interface ScanResult {
  summary: string;
  added: number;
  updated: number;
  unchanged: number;
  skippedPlaceholder: number;
  conflicts: string[];
  errors: { path: string; error: string }[];
  error?: string;
  /** Songs listed and still being read in the background, then analysed. */
  identifying?: number;
  /** Files skipped because Nova cannot read their format, by extension. */
  unsupported?: Record<string, number>;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${engineUrl}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return (await res.json()) as T;
}

export function useLibrary() {
  /** Serialise the current scope for a tracks request. */
  const scopeQuery = () => {
    const { rootId, folder, genre } = scopeRef.current;
    const parts: string[] = [];
    if (rootId) parts.push(`rootId=${rootId}`);
    if (folder) parts.push(`folder=${encodeURIComponent(folder)}`);
    if (genre) parts.push(`genre=${encodeURIComponent(genre)}`);
    // A clicked column header: the engine sorts the whole library, not the loaded page.
    if (sortRef.current) parts.push(`sort=${encodeURIComponent(sortRef.current.field)}&dir=${sortRef.current.dir}`);

    // The AST goes to the engine, which compiles it to SQL. The client parser
    // stays the only parser -- this is its output, not a second grammar.
    const ast = queryRef.current;
    if (ast) parts.push(`q=${encodeURIComponent(JSON.stringify(ast))}`);

    return parts.length ? `&${parts.join("&")}` : "";
  };

  const [state, setState] = useState<EngineState>("connecting");
  const [tracks, setTracks] = useState<Track[]>([]);
  const [status, setStatus] = useState<EngineStatus | null>(null);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState<ScanResult | null>(null);
  const [total, setTotal] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [columns, setColumns] = useState<CustomColumn[]>([]);
  const [settings, setSettings] = useState<SettingsPayload | null>(null);
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [genres, setGenres] = useState<GenreFacet[]>([]);
  const [pendingExcluded, setPendingExcluded] = useState(0);
  /**
   * Which folder the grid is scoped to.
   *
   * A ref rather than state: refresh() reads it, and making it a dependency
   * would rebuild refresh on every scope change and restart the polling effect.
   */
  const scopeRef = useRef<{ rootId?: number; folder?: string; genre?: string }>({});
  /** The same scope as state, with a name to show, so the grid can say it is narrowed and clear it. */
  const [scope, setScopeState] = useState<{ rootId?: number; folder?: string; genre?: string; label?: string }>({});
  /**
   * The parsed search, sent to the engine so filtering happens across the whole
   * library rather than the loaded page.
   *
   * A ref for the same reason as the scope: refresh() reads it, and making it a
   * dependency would rebuild refresh on every keystroke.
   */
  const queryRef = useRef<unknown>(null);
  /** The engine field and direction the grid is sorted by; a ref for the same reason. */
  const sortRef = useRef<{ field: string; dir: "asc" | "desc" } | null>(null);
  // A ref, not state: refresh() reads it and must not be re-created when it
  // changes, or the polling effect would restart on every page load.
  const loadedRef = useRef(0);

  const refresh = useCallback(async () => {
    try {
      engineUrl = (await engineInfo()).url;
      const [s, page, cols, prefs, folders, facets] = await Promise.all([
        call<EngineStatus>("/api/status"),
        // Re-fetch everything already on screen, so a refresh after a save does
        // not silently collapse the list back to the first page.
        call<TracksPage>(
          `/api/tracks?limit=${Math.max(PAGE_SIZE, loadedRef.current)}${scopeQuery()}`
        ),
        call<{ columns: CustomColumn[] }>("/api/columns").catch(() => ({ columns: [] })),
        // Settings are optional: an engine without the endpoint yet still works,
        // it just cannot be reconfigured. The catch returns the same shape so the
        // success and failure paths stay type-identical.
        // Settings are optional: an engine without the endpoint still works, it
        // just cannot be reconfigured.
        call<SettingsPayload>("/api/settings").catch(() => null),
        // The library folders and one level of folders in each; deeper ones load as
        // they are opened (a library of millions has hundreds of thousands of folders).
        call<{ roots: TreeNode[] }>(`/api/tree?depth=1&children=${TREE_CHILDREN}`).catch(() => ({ roots: [] })),
        call<{ genres: GenreFacet[] }>("/api/genres").catch(() => ({ genres: [] })),
      ]);
      setStatus(s);
      setTracks(page.tracks);
      setTotal(page.total);
      setTotalSeconds(page.totalSeconds ?? null);
      setPendingExcluded(page.pendingExcluded ?? 0);
      setHasMore(page.hasMore);
      setColumns(cols.columns);
      setSettings(prefs);
      setTree(folders.roots);
      setGenres(facets.genres);
      loadedRef.current = page.tracks.length;
      setState("connected");
    } catch {
      // Engine not running: fall back to seed data, labelled in the UI.
      setState("offline");
      setTracks(SEED_TRACKS);
      setTotal(SEED_TRACKS.length);
      setHasMore(false);
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // No polling: the app refreshes when the engine announces a change
  // (see core/app.tsx and core/engineEvents.ts).

  const scan = useCallback(
    async (path: string) => {
      setScanning(true);
      try {
        const result = await call<ScanResult>("/api/scan", {
          method: "POST",
          // List the folder and return; the engine reads the songs through
          // after, saying how far it has got (library.identifying).
          body: JSON.stringify({ path, background: true }),
        });
        setLastScan(result);
        if (!result.error) await refresh();
        return result;
      } catch (err) {
        const failure: ScanResult = {
          summary: "",
          added: 0,
          updated: 0,
          unchanged: 0,
          skippedPlaceholder: 0,
          conflicts: [],
          errors: [],
          error: err instanceof Error ? err.message : String(err),
        };
        setLastScan(failure);
        return failure;
      } finally {
        setScanning(false);
      }
    },
    [refresh]
  );

  /**
   * Re-read just these songs, where they are already on screen (analysis has
   * filled in their values), instead of reloading every loaded row. Songs not
   * loaded are left: they come fresh when their page is.
   */
  const tracksRef = useRef<Track[]>([]);
  tracksRef.current = tracks;
  const patchRows = useCallback(async (ids: string[]) => {
    const loaded = new Set(tracksRef.current.map((t) => t.id));
    const wanted = [...new Set(ids)].filter((i) => loaded.has(i));
    if (!wanted.length) return;
    const fresh = new Map<string, Track>();
    try {
      for (let i = 0; i < wanted.length; i += 200) {
        const chunk = wanted.slice(i, i + 200);
        const page = await call<TracksPage>(`/api/tracks?ids=${chunk.join(",")}&limit=${chunk.length}`);
        for (const t of page.tracks) fresh.set(t.id, t);
      }
    } catch {
      return;
    }
    if (fresh.size) setTracks((prev) => prev.map((t) => fresh.get(t.id) ?? t));
  }, []);

  /**
   * Append the next page. The engine orders by a total key, so pages never overlap.
   * ``upTo``: the row count wanted (the list scrolled far down), fetched in one
   * bigger page, up to the engine's 1000, rather than many small ones.
   */
  const loadMore = useCallback(async (upTo?: number) => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const want = Math.min(1000, Math.max(PAGE_SIZE, (upTo ?? 0) - loadedRef.current));
      const page = await call<TracksPage>(
        `/api/tracks?limit=${want}&offset=${loadedRef.current}${scopeQuery()}`
      );
      setTracks((prev) => [...prev, ...page.tracks]);
      loadedRef.current += page.tracks.length;
      setTotal(page.total);
      setTotalSeconds(page.totalSeconds ?? null);
      setPendingExcluded(page.pendingExcluded ?? 0);
      setHasMore(page.hasMore);
    } catch {
      // Leave what is already loaded on screen; the button stays available.
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore]);

  /**
   * Rescan specific tracks rather than a whole folder.
   *
   * Re-reads tags and re-checks the payload hash. If the audio changed the engine
   * re-queues analysis; if it did not, the existing analysis stands, because it is
   * keyed by that hash and is still correct.
   */
  const rescanTracks = useCallback(
    async (trackIds: string[]): Promise<string | null> => {
      if (trackIds.length === 0) return null;
      setRescanning(true);
      try {
        const res = await call<{ ok?: boolean; error?: string }>("/api/rescan", {
          method: "POST",
          body: JSON.stringify({ trackIds: trackIds.map(Number) }),
        });
        await refresh();
        return res.error ?? null;
      } catch (err) {
        return err instanceof Error ? err.message : String(err);
      } finally {
        setRescanning(false);
      }
    },
    [refresh]
  );

  /** Folder-level actions. Each refreshes, because all of them change the grid. */
  /**
   * Scope the grid to a root, a folder, or the whole library.
   *
   * Server-side: a folder is a property of the library, not of the page. Doing
   * this in the client filtered only the rows already fetched, so picking a
   * folder outside the current page showed nothing at all.
   */
  /** Send a new search to the engine and reload from the first page. */
  const setQuery = useCallback(
    async (ast: unknown) => {
      queryRef.current = ast;
      loadedRef.current = 0;
      await refresh();
    },
    [refresh]
  );

  const setScope = useCallback(
    async (next: { rootId?: number; folder?: string; genre?: string; label?: string }) => {
      const { label: _label, ...query } = next;
      scopeRef.current = query;
      setScopeState(next);
      loadedRef.current = 0;
      await refresh();
    },
    [refresh]
  );

  /** Sort the grid by an engine field (null: back to the library's own order). */
  const setSort = useCallback(
    async (next: { field: string; dir: "asc" | "desc" } | null) => {
      sortRef.current = next;
      loadedRef.current = 0;
      await refresh();
    },
    [refresh]
  );

  const folderAction = useCallback(
    async (
      action: "rescan" | "archive" | "remove" | "restore",
      body: Record<string, unknown>
    ): Promise<string | null> => {
      const route = {
        rescan: "/api/roots/rescan",
        archive: "/api/tracks/archive",
        remove: "/api/roots/remove",
        restore: "/api/roots/restore",
      }[action];
      try {
        const res = await call<{ ok?: boolean; error?: string }>(route, {
          method: "POST",
          body: JSON.stringify(action === "rescan" ? { ...body, background: true } : body),
        });
        await refresh();
        return res.error ?? null;
      } catch (err) {
        return err instanceof Error ? err.message : String(err);
      }
    },
    [refresh]
  );

  const createColumn = useCallback(
    async (label: string, type: CustomColumn["type"]): Promise<string | null> => {
      try {
        const res = await call<{ ok?: boolean; error?: string }>("/api/columns/create", {
          method: "POST",
          body: JSON.stringify({ label, type }),
        });
        if (res.error) return res.error;
        await refresh();
        return null;
      } catch (err) {
        return err instanceof Error ? err.message : String(err);
      }
    },
    [refresh]
  );

  const deleteColumn = useCallback(
    async (key: string) => {
      try {
        await call("/api/columns/delete", {
          method: "POST",
          body: JSON.stringify({ key }),
        });
        await refresh();
      } catch {
        // Nothing useful to do; the column list is re-read on the next refresh.
      }
    },
    [refresh]
  );

  const setColumnValue = useCallback(
    async (trackId: string, key: string, value: unknown) => {
      try {
        await call("/api/columns/value", {
          method: "POST",
          body: JSON.stringify({ trackId: Number(trackId), key, value }),
        });
        await refresh();
      } catch {
        // Same: the value is re-read from the engine on refresh.
      }
    },
    [refresh]
  );

  const saveSettings = useCallback(
    async (next: Record<string, string[]>): Promise<string | null> => {
      try {
        const res = await call<{ ok?: boolean; error?: string }>("/api/settings", {
          method: "POST",
          body: JSON.stringify({ settings: next }),
        });
        if (res.error) return res.error;
        await refresh();
        return null;
      } catch (err) {
        return err instanceof Error ? err.message : String(err);
      }
    },
    [refresh]
  );

  const saveWatch = useCallback(
    async (changes: {
      enabled?: boolean;
      sweepMinutes?: number;
    }): Promise<string | null> => {
      try {
        const res = await call<{ ok?: boolean; error?: string }>("/api/watch", {
          method: "POST",
          body: JSON.stringify(changes),
        });
        if (res.error) return res.error;
        await refresh();
        return null;
      } catch (err) {
        return err instanceof Error ? err.message : String(err);
      }
    },
    [refresh]
  );

  /**
   * Ask for a reconciliation sweep now.
   *
   * The engine queues it and answers immediately -- a sweep walks every root and
   * can take a while on a large library, so holding the request open would only
   * hand the caller a timeout to deal with. The result shows up in the status
   * the next time it is polled.
   */
  const sweepNow = useCallback(async (): Promise<string | null> => {
    try {
      await call("/api/watch/sweep", { method: "POST", body: "{}" });
      return null;
    } catch (err) {
      return err instanceof Error ? err.message : String(err);
    }
  }, []);

  /**
   * Playlists.
   *
   * Every mutation returns the whole playlist rather than a patch, so the screen
   * never has to reconstruct what the server did — positions, durations and the
   * transition between every pair all move together, and a reorder that the
   * server refused cannot leave the UI showing an order that does not exist.
   */
  const [playlists, setPlaylists] = useState<PlaylistSummary[]>([]);
  const [playlist, setPlaylist] = useState<Playlist | null>(null);

  const refreshPlaylists = useCallback(async () => {
    try {
      setPlaylists((await call<{ playlists: PlaylistSummary[] }>("/api/playlists")).playlists);
    } catch {
      setPlaylists([]);
    }
  }, []);

  const openPlaylist = useCallback(async (id: number | null) => {
    if (id === null) {
      setPlaylist(null);
      return;
    }
    try {
      setPlaylist((await call<{ playlist: Playlist }>(`/api/playlists?id=${id}`)).playlist);
    } catch {
      setPlaylist(null);
    }
  }, []);

  const playlistAction = useCallback(
    async (path: string, body: Record<string, unknown>): Promise<string | null> => {
      try {
        const res = await call<{ error?: string; warning?: string | null; playlist?: Playlist }>(
          `/api/playlists/${path}`,
          { method: "POST", body: JSON.stringify(body) }
        );
        if (res.error) return res.error;
        if (res.playlist) setPlaylist(res.playlist);
        await refreshPlaylists();
        return res.warning ?? null;
      } catch (err) {
        return err instanceof Error ? err.message : String(err);
      }
    },
    [refreshPlaylists]
  );

  return {
    state, tracks, status, scan, scanning, lastScan, refresh, patchRows,
    settings, saveSettings, saveWatch, sweepNow,
    playlists, playlist, refreshPlaylists, openPlaylist, playlistAction,
    total, totalSeconds, hasMore, loadMore, loadingMore,
    rescanTracks, rescanning,
    tree, genres, folderAction, scope, setScope, setSort, setQuery, pendingExcluded,
    columns, createColumn, deleteColumn, setColumnValue,
  };
}

export interface TagWriteResult {
  ok?: boolean;
  payloadHash?: string;
  payloadIntact?: boolean;
  bytesBefore?: number;
  bytesAfter?: number;
  warnings?: string[];
  reindexed?: string;
  /** Present when the write was refused or rolled back. */
  error?: string;
  reverted?: boolean;
}

/**
 * Write tag edits to a file.
 *
 * Only changed fields are sent. A network failure is returned as an error rather
 * than thrown, because the caller renders it next to the Save button and there is
 * nothing useful to do with an exception there.
 */
export async function writeTags(
  trackId: string,
  fields: Record<string, string | null>,
  custom: Record<string, string | null>
): Promise<TagWriteResult> {
  try {
    return await call<TagWriteResult>("/api/tags", {
      method: "POST",
      body: JSON.stringify({ trackId, fields, custom }),
    });
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/** Cover art as the engine accepts it: base64 bytes and their type. */
export interface ArtworkUpload {
  data: string;
  mime: "image/jpeg" | "image/png";
}

/**
 * Replace a track's cover art, or remove it with `null`.
 *
 * Goes through the same endpoint and SafeTagWriter as every tag edit, so the
 * audio payload is verified untouched. Replacing removes every existing picture
 * and writes one front cover.
 */
export async function writeArtwork(
  trackId: string,
  artwork: ArtworkUpload | null
): Promise<TagWriteResult> {
  try {
    return await call<TagWriteResult>("/api/tags", {
      method: "POST",
      body: JSON.stringify(
        artwork ? { trackId, artwork } : { trackId, removeArtwork: true }
      ),
    });
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Discard curator overrides and restore what Nova originally detected.
 *
 * The detected value was never overwritten -- a curator edit goes to the file's
 * tag while the engine's reading stays in the analysis table, held in trust and
 * simply outranked. So reverting clears the tag rather than restoring a backup.
 */
/** Shared shape of every bulk operation's response. */
export interface BulkResult {
  ok?: boolean;
  dryRun?: boolean;
  requested?: number;
  failed?: number;
  skipped?: number;
  /** Past tense when it ran; the would-* twin when it was a dry run. */
  written?: number;
  wouldWrite?: number;
  queued?: number;
  wouldQueue?: number;
  changed?: number;
  wouldChange?: number;
  deleted?: number;
  wouldDelete?: number;
  /** Delete says this three ways; none of them ever becomes true. */
  deletesFiles?: boolean;
  filesDeleted?: number;
  stillOnDisk?: number;
  playlistItemsOrphaned?: number;
  results?: {
    trackId: number | string;
    path?: string;
    status: string;
    error?: string;
    reason?: string;
  }[];
  error?: string;
}

async function bulk(path: string, body: Record<string, unknown>): Promise<BulkResult> {
  try {
    return await call<BulkResult>(path, { method: "POST", body: JSON.stringify(body) });
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Bulk operations on a selection.
 *
 * Each takes `dryRun` so the UI can say "this will affect 47 tracks, 3 of which
 * are read-only" before anything happens. The response key set is identical
 * either way -- only `written` vs `wouldWrite` differs -- so a preview cannot be
 * mistaken for a result.
 */
export const bulkTag = (
  trackIds: string[],
  fields: Record<string, string | null>,
  custom: Record<string, string | null> = {},
  dryRun = false
) => bulk("/api/tracks/tag", { trackIds, fields, custom, dryRun });

/** Several curator columns on many tracks, in one transaction. Opens no files. */
export const bulkCustomValues = (
  trackIds: string[],
  values: Record<string, string | number | boolean | null>,
  dryRun = false
) => bulk("/api/columns/values", { trackIds, values, dryRun });

/**
 * Re-queue analysis. Energy and danceability are only redone when
 * `includeDynamics` is set: the same audio through the same method cannot rate
 * differently, so by default only BPM, key and loudness (and the spectrum, when it is on) are redone.
 */
export const bulkAnalyze = (trackIds: string[], dryRun = false, includeDynamics = false) =>
  bulk("/api/tracks/analyze", { trackIds, dryRun, includeDynamics });

export const bulkArchive = (trackIds: string[], archived = true, dryRun = false) =>
  bulk("/api/tracks/archive", { trackIds, archived, dryRun });

/** Removes index rows only. Nova never deletes a curator's audio. */
export const bulkDelete = (trackIds: string[], dryRun = false) =>
  bulk("/api/tracks/delete", { trackIds, dryRun });

export async function revertToDetected(
  trackId: string,
  fields?: ("bpm" | "key" | "energy")[]
): Promise<{ ok?: boolean; error?: string }> {
  try {
    return await call("/api/tags/revert", {
      method: "POST",
      body: JSON.stringify({ trackId, fields }),
    });
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

export interface TrackDetail {
  trackId: string;
  path: string;
  container: string;
  /** Standard fields, keyed by Nova's logical field name. */
  fields: Record<string, string>;
  /** Nova's namespaced keys, prefix stripped. */
  nova: Record<string, string>;
  /** Keys the container holds that Nova does not model. */
  custom: Record<string, string>;
  hasArtwork: boolean;
  error?: string;
  available?: boolean;
}

/** Read every tag on one file, straight from disk. */
export async function fetchTrackDetail(trackId: string): Promise<TrackDetail | null> {
  try {
    return await call<TrackDetail>(`/api/track?id=${encodeURIComponent(trackId)}`);
  } catch {
    return null;
  }
}

// ------------------------------------------------------------------ cue editor

/** The engine's base URL, for things a browser element loads itself (audio). */
export function engineBase(): string {
  return engineUrl;
}

/**
 * A track's cover image. `version` changes the URL after an edit, so the
 * webview cannot show a bitmap it decoded for the previous cover.
 */
export function artworkUrl(trackId: string, version = ""): string {
  const v = version ? `&v=${encodeURIComponent(version)}` : "";
  return withToken(`${engineUrl}/api/track/artwork?id=${encodeURIComponent(trackId)}${v}`);
}

/** Streamed with Range support, so the webview never decodes the whole file. */
export function audioUrl(trackId: string): string {
  return withToken(`${engineUrl}/api/track/audio?id=${encodeURIComponent(trackId)}`);
}

export interface Peaks {
  version: string;
  duration: number;
  perSecond: number;
  /** Signed ints in -127..127; divide by 127 for the canvas. */
  peaks: number[];
  error?: string;
}

export async function fetchPeaks(trackId: string, signal?: AbortSignal): Promise<Peaks> {
  try {
    const res = await fetch(`${engineUrl}/api/track/peaks?id=${encodeURIComponent(trackId)}`, {
      signal,
    });
    return (await res.json()) as Peaks;
  } catch (err) {
    return {
      version: "",
      duration: 0,
      perSecond: 0,
      peaks: [],
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

export interface CueResponse {
  trackId?: string;
  cues?: CueState;
  beatgrid?: BeatGridState | null;
  /** Cue points saved, but mirroring them into the file's tags did not happen. */
  tagWarning?: string | null;
  error?: string;
}

export async function fetchCues(trackId: string): Promise<CueResponse> {
  try {
    return await call<CueResponse>(`/api/track/cues?id=${encodeURIComponent(trackId)}`);
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/** Set or clear a person's beat grid. Nova's database only; the file is never touched. */
export async function saveBeatGrid(
  trackId: string,
  changes: { bpm?: number; downbeat?: number },
  clear: ("bpm" | "downbeat")[] = []
): Promise<{ beatgrid?: BeatGridState; error?: string }> {
  try {
    return await call("/api/track/beatgrid", {
      method: "POST",
      body: JSON.stringify({ id: Number(trackId), changes, clear }),
    });
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/** The grid locked to the audio near a rough tempo (a tap). Nothing is saved. */
export async function detectBeatGrid(trackId: string, bpm: number): Promise<{ found?: BeatGridFound; error?: string }> {
  try {
    return await call("/api/track/beatgrid/detect", {
      method: "POST",
      body: JSON.stringify({ id: Number(trackId), bpm }),
    });
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Set or clear cue points. `ending` omitted leaves it alone; `null` clears it.
 * The engine checks the whole resulting arrangement and names the rule a
 * refused change breaks.
 */
export async function saveCues(
  trackId: string,
  changes: Partial<Record<CueField, number>>,
  clear: CueField[] = [],
  ending?: Ending | null
): Promise<CueResponse> {
  const body: Record<string, unknown> = { id: Number(trackId), changes, clear };
  if (ending !== undefined) body.ending = ending;
  try {
    return await call<CueResponse>("/api/track/cues", {
      method: "POST",
      body: JSON.stringify(body),
    });
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/** Credited artists in the library, split like the grid's chips, with counts. */
export async function fetchArtists(q: string, limit = 50): Promise<{ name: string; tracks: number }[]> {
  try {
    const res = await call<{ artists: { name: string; tracks: number }[] }>(
      `/api/artists?q=${encodeURIComponent(q)}&limit=${limit}`
    );
    return res.artists ?? [];
  } catch {
    return [];
  }
}

export interface TrackGroup {
  id: number;
  name: string;
  tracks: number;
}

export async function fetchGroups(): Promise<{ groups: TrackGroup[] }> {
  try {
    return await call("/api/groups");
  } catch {
    return { groups: [] };
  }
}

/** Delete a track group; its songs stay, only their membership goes. */
export async function deleteGroup(name: string): Promise<{ ok?: boolean; error?: string; membershipsRemoved?: number }> {
  try {
    return await call("/api/groups/delete", { method: "POST", body: JSON.stringify({ name }) });
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

export async function assignGroups(
  trackIds: string[],
  groups: string[],
  mode: "add" | "replace" | "remove" = "add"
): Promise<{ ok?: boolean; created?: string[]; error?: string }> {
  try {
    return await call("/api/groups/assign", {
      method: "POST",
      body: JSON.stringify({ trackIds, groups, mode }),
    });
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

export interface PlaylistFile {
  ok?: boolean;
  fileName?: string;
  content?: string;
  songs?: number;
  error?: string;
}

export interface Candidate {
  trackId: string;
  title: string | null;
  artist: string | null;
  duration: number | null;
  bpm: number | null;
  energyLevel: number | null;
  danceability: number | null;
  lufs: number | null;
  camelot: string | null;
  /** 0-100 against the slot's targets; null when there is nothing to rank against. */
  match: number | null;
  /** From the song before the slot, when there is one. */
  transition?: { keyRelation: string; bpmDrift: number | null; tempoRelation: string; harmonic: boolean };
}

export interface CandidateResult {
  candidates: Candidate[];
  total: number;
  slot?: { t: number | null; itemId: number | null; index: number | null; title: string | null; previousTitle: string | null };
  targets?: Record<string, number>;
  targetsFrom?: "client" | "curves" | "track" | "none";
  error?: string;
}

export interface ScheduleFacets {
  eras: { label: string; from: number; to: number; tracks: number }[];
  rotations: { name: string; tracks: number }[];
}

/** Checkbox lists for Flow's filters: decades and track groups. */
export async function fetchScheduleFacets(): Promise<ScheduleFacets> {
  try {
    const res = await call<Partial<ScheduleFacets>>("/api/scheduler/facets");
    // An engine without this endpoint answers with an error object, not a throw.
    return { eras: Array.isArray(res.eras) ? res.eras : [], rotations: Array.isArray(res.rotations) ? res.rotations : [] };
  } catch {
    return { eras: [], rotations: [] };
  }
}

/** Songs closest to some target values, best Match first. Read-only. */
export async function fetchCandidates(body: Record<string, unknown>, signal?: AbortSignal): Promise<CandidateResult> {
  try {
    return await call<CandidateResult>("/api/scheduler/candidates", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    return { candidates: [], total: 0, error: err instanceof Error ? err.message : String(err) };
  }
}

/** A playlist as an Extended M3U (UTF-8, absolute paths), to save anywhere. */
export async function exportPlaylistM3U(id: number): Promise<PlaylistFile> {
  try {
    return await call<PlaylistFile>("/api/playlists/export", { method: "POST", body: JSON.stringify({ id }) });
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}


// ------------------------------------------------------------------ analysis queue

export interface JobKind {
  kind: string;
  label: string;
  queued: number;
  running: number;
  failed: number;
  done: number;
}

export interface JobReport {
  paused: boolean;
  workers: number;
  /** The most songs that may be analysed at once (every core but one). */
  workersMax?: number;
  /** The most that leave the computer free for other work (half the cores, memory allowing). */
  workersSafe?: number;
  active: number;
  /** Jobs still to run. Two passes per track, so this is ~2x the track count. */
  outstanding: number;
  failed: number;
  kinds: JobKind[];
  /** Each pass on its own, in order: songs done of songs there are (per audio). */
  steps?: { kind: string; label: string; done: number; total: number; running: number; queued: number; failed: number }[];
  /** The same thing counted in music, which is what a curator recognises. */
  /** ``identifying``: listed by a scan, not yet read through (counted in ``waiting`` too). */
  tracks: { total: number; waiting: number; identifying?: number };
  /** Set by a clear: whether the next scan will queue this work again. */
  willReturn?: boolean;
  stopped?: boolean;
  removed?: number;
  requeued?: number;
}

/** What analysis is outstanding. Null when the engine cannot be reached. */
export async function fetchJobs(): Promise<JobReport | null> {
  try {
    return await call<JobReport>("/api/jobs");
  } catch {
    return null;
  }
}

async function jobAction(path: string, body: Record<string, unknown> = {}): Promise<JobReport | null> {
  try {
    return await call<JobReport>(path, { method: "POST", body: JSON.stringify(body) });
  } catch {
    return null;
  }
}

/** Stop or resume analysis. Tracks already being analysed finish either way. */
export const pauseJobs = (paused: boolean) => jobAction("/api/jobs/pause", { paused });

/**
 * Stop analysing: pause AND empty the queue.
 *
 * The difference from pausing is the backlog. Resuming after a pause carries on
 * where it left off; resuming after a stop starts from whatever is missing then.
 */
export const stopJobs = () => jobAction("/api/jobs/stop");

/** ffmpeg: whether the engine has it, and how far its download has got (nova/tools.py). */
export interface ToolsReport {
  ready: boolean;
  found: Record<string, string | null>;
  /** The build Nova can fetch on this computer; null where there is none (macOS). */
  download: string | null;
  /** How to install it by hand where Nova cannot fetch it. */
  hint: string | null;
  progress: { phase: "idle" | "downloading" | "checking" | "done" | "failed"; downloaded: number; total: number | null; error: string | null };
}

export async function fetchTools(): Promise<ToolsReport | null> {
  try {
    return await call<ToolsReport>("/api/tools");
  } catch {
    return null;
  }
}

/** Start fetching ffmpeg into Nova's tools folder; `fetchTools` follows it. */
export async function downloadFfmpeg(): Promise<{ ok?: boolean; error?: string }> {
  try {
    return await call("/api/tools/ffmpeg/download", { method: "POST", body: "{}" });
  } catch (err) {
    return { error: String(err) };
  }
}

/** DeepRhythm, the optional trained tempo detector (nova/extras.py). */
export interface DeepRhythmStatus {
  /** The person's choice; null when they never chose. */
  setting: boolean | null;
  /** Whether the next analysis will use it. */
  active: boolean;
  installed: {
    packages: Record<string, string>;
    bytes: number;
    installedAt: string;
    /** The load test run after the download (and on "Check again"). */
    check?: { ok: boolean; error?: string; seconds: number; checkedAt: string };
  } | null;
  importable: boolean;
  download: { phase: "idle" | "downloading" | "checking" | "done" | "failed"; downloaded: number; files: number; current: string | null; error: string | null };
  sizeHint: string;
}

export interface BpmComparison {
  audioSha256: string;
  trackId: string | null;
  title: string;
  artist: string | null;
  status: "queued" | "measuring" | "done" | "failed";
  error: string | null;
  /** While measuring: the stage it is on ("loading DeepRhythm"), and for how long. */
  step: string | null;
  stepSeconds: number | null;
  novaBpm: number | null;
  novaConfidence: number | null;
  deepBpm: number | null;
  deepConfidence: number | null;
  agree: boolean;
  tagBpm: number | null;
  verdict: "nova" | "deeprhythm" | "both" | "neither" | null;
  trueBpm: number | null;
}

export interface BpmCompareResult {
  songs: BpmComparison[];
  tally: { compared: number; agree: number; differ: number; checked: number; novaRight: number; deepRight: number };
  agreePercent: number;
  progress: {
    total: number;
    done: number;
    failed: number;
    measuring: number;
    waiting: number;
    /** Average over the last songs measured; null until one is done. */
    secondsPerSong: number | null;
    /** Why nothing is moving: "paused", or a sentence; null when it is not stuck. */
    waitingReason: string | null;
  };
}

const quiet = async <T,>(path: string, init?: RequestInit): Promise<T | null> => {
  try {
    return await call<T>(path, init);
  } catch {
    return null;
  }
};
const post = <T,>(path: string, body: unknown = {}) => quiet<T>(path, { method: "POST", body: JSON.stringify(body) });

/** Songs by id, wherever they are in the library (not only the loaded pages). */
export async function fetchTracksByIds(ids: string[]): Promise<Track[]> {
  try {
    return (await call<TracksPage>(`/api/tracks?ids=${ids.join(",")}&limit=${ids.length}`)).tracks;
  } catch {
    return [];
  }
}

export const fetchDeepRhythm = () => quiet<DeepRhythmStatus>("/api/deeprhythm");
export const setDeepRhythm = (enabled: boolean) => post<DeepRhythmStatus>("/api/deeprhythm", { enabled });
export const downloadDeepRhythm = () => post<DeepRhythmStatus>("/api/deeprhythm/download");
export const removeDeepRhythm = () => post<DeepRhythmStatus>("/api/deeprhythm/remove");
export const checkDeepRhythm = () => post<DeepRhythmStatus>("/api/deeprhythm/check");
/** Is analysis moving: the process, what it is on and for how long. */
export interface AnalysisHealth {
  ffmpegReady: boolean;
  paused: boolean;
  workers: number;
  active: number;
  queued: number;
  running: { kind: string; song: string | null; seconds: number; attempt: number }[];
  process: {
    running: boolean;
    pid?: number | null;
    asleep?: boolean;
    restarts?: number;
    inProcess?: boolean;
    /** It kept stopping and Nova stopped restarting it: its exit code and last lines. */
    broken?: { code: number | null; lines: string[]; at: string } | null;
  };
}
export const fetchAnalysisHealth = () => quiet<AnalysisHealth>("/api/jobs/health");
/** End the analysis process, however stuck, and start a fresh one. */
export const restartAnalysis = () => post<AnalysisHealth & { ok?: boolean; error?: string }>("/api/jobs/restart");

/** The analysis process's own log; `dumpAnalysis` first adds where every thread is. */
export const fetchAnalysisLog = (lines = 150) => quiet<{ path: string | null; lines: string[] }>(`/api/deeprhythm/log?lines=${lines}`);
export const dumpAnalysis = () => post<{ ok: boolean }>("/api/deeprhythm/dump");
export const compareBpm = (trackIds: string[]) => post<{ queued?: number; error?: string }>("/api/deeprhythm/compare", { trackIds });
export const fetchBpmComparison = () => quiet<BpmCompareResult>("/api/deeprhythm/compare");
export const setBpmVerdict = (audioSha256: string, verdict: BpmComparison["verdict"], trueBpm?: number | null) =>
  post<{ ok?: boolean; error?: string }>("/api/deeprhythm/compare/verdict", { audioSha256, verdict, trueBpm: trueBpm ?? null });
export const clearBpmComparison = () => post<{ removed: number }>("/api/deeprhythm/compare/clear");

/** How many songs are analysed at once, from now on (kept by the engine). */
export const setJobWorkers = (workers: number) => jobAction("/api/jobs/workers", { workers });

/** How much a song's analysis works out (Settings -> Library); applies from the next song. */
export interface AnalysisOptions {
  depth: "fast" | "deep";
  spectrum: boolean;
  /** Whose detected Cue In and Early Out the players use. */
  cues: "nova" | "autocue";
}

export async function fetchAnalysisOptions(): Promise<AnalysisOptions | null> {
  try {
    return await call<AnalysisOptions>("/api/analysis/options");
  } catch {
    return null;
  }
}

export async function saveAnalysisOptions(change: Partial<AnalysisOptions>): Promise<AnalysisOptions | null> {
  try {
    const r = await call<AnalysisOptions & { error?: string }>("/api/analysis/options", { method: "POST", body: JSON.stringify(change) });
    return r.error ? null : r;
  } catch {
    return null;
  }
}

/** Make a folder `name` inside `folder` (root-relative, "" for the library folder itself). */
export async function createFolder(rootId: number, folder: string, name: string): Promise<{ path?: string; error?: string }> {
  try {
    const res = await fetch(`${engineUrl}/api/folders/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rootId, folder, name }),
    });
    return (await res.json()) as { path?: string; error?: string };
  } catch {
    return { error: "The engine is not answering." };
  }
}

/** Delete an empty folder from disk; the engine refuses one with anything in it. */
/** What adding one song by hand should warn about. */
export interface TrackAvailability {
  manualOnly: boolean;
  folder: string | null;
  availableFrom: string | null;
  released: boolean;
}

async function postJson<T>(path: string, body: unknown): Promise<T & { error?: string }> {
  try {
    const res = await fetch(`${engineUrl}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return (await res.json()) as T & { error?: string };
  } catch {
    return { error: "The engine is not answering." } as T & { error?: string };
  }
}

/** A folder's songs only play when a person picks them (Auto DJ and Flow leave them out). */
export function setFolderManualOnly(rootId: number, folder: string, manualOnly: boolean) {
  return postJson<{ ok?: boolean }>("/api/availability/folder", { rootId, folder, manualOnly });
}

/** Songs' own setting: hand-picked only, and/or a release date and time ("YYYY-MM-DDTHH:MM", null clears). */
export function setSongsAvailability(
  trackIds: (string | number)[],
  change: { manualOnly?: boolean; availableFrom?: string | null },
) {
  return postJson<{ ok?: boolean; songs?: number }>("/api/availability/songs", { trackIds, ...change });
}

export async function trackAvailability(trackId: string | number): Promise<TrackAvailability | null> {
  try {
    const res = await fetch(`${engineUrl}/api/availability?trackId=${encodeURIComponent(String(trackId))}`);
    const body = (await res.json()) as { track?: TrackAvailability };
    return body.track ?? null;
  } catch {
    return null;
  }
}

export async function deleteFolder(rootId: number, folder: string): Promise<{ path?: string; error?: string }> {
  try {
    const res = await fetch(`${engineUrl}/api/folders/delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rootId, folder }),
    });
    return (await res.json()) as { path?: string; error?: string };
  } catch {
    return { error: "The engine is not answering." };
  }
}

/**
 * Copy songs from this computer into a library folder, one after another, and
 * have them indexed. The file goes as the request body, so a big song is never
 * held whole in memory on either side. Never replaces a file: a name already
 * taken gets " (2)" added.
 */
export async function uploadSongs(
  rootId: number,
  folder: string,
  files: File[],
  onProgress?: (done: number, total: number, name: string) => void,
): Promise<{ copied: string[]; failed: { name: string; error: string }[] }> {
  const copied: string[] = [];
  const failed: { name: string; error: string }[] = [];
  for (const [i, file] of files.entries()) {
    onProgress?.(i, files.length, file.name);
    const url = `${engineUrl}/api/folders/upload?rootId=${rootId}&folder=${encodeURIComponent(folder)}&name=${encodeURIComponent(file.name)}`;
    try {
      const res = await fetch(url, { method: "POST", headers: { "Content-Type": "application/octet-stream" }, body: file });
      const out = (await res.json()) as { name?: string; error?: string };
      if (out.error) failed.push({ name: file.name, error: out.error });
      else copied.push(out.name ?? file.name);
    } catch {
      failed.push({ name: file.name, error: "the engine stopped answering" });
    }
  }
  onProgress?.(files.length, files.length, "");
  return { copied, failed };
}

/** Throw away queued work. Nothing already analysed is lost. */
export const clearJobs =(scope: "queued" | "failed" | "finished" | "all" = "queued") =>
  jobAction("/api/jobs/clear", { scope });

/** Put failed jobs back in the queue. */
export const retryJobs = () => jobAction("/api/jobs/retry");

// ---------------------------------------------------------------- needs edit and the inbox

/** Why a song was flagged (library/needs_edit.py): who, when, and a note. */
export interface NeedsEditFlag {
  reason: string;
  note: string | null;
  by: string | null;
  at: string;
}

export interface NeedsEditSong extends NeedsEditFlag {
  trackId: string;
  title: string;
  artist: string;
}

export interface Notice {
  id: number;
  key: string | null;
  source: string;
  title: string;
  body: string | null;
  view: string | null;
  params: Record<string, string> | null;
  createdAt: string;
  updatedAt: string;
}

export async function fetchNeedsEdit(reason?: string): Promise<{ songs: NeedsEditSong[]; reasons: string[] }> {
  try {
    return await call(`/api/needs-edit${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`);
  } catch {
    return { songs: [], reasons: [] };
  }
}

export async function markNeedsEdit(ids: string[], reason: string, note?: string, by?: string): Promise<{ error?: string; flagged?: number }> {
  try {
    return await call("/api/needs-edit", { method: "POST", body: JSON.stringify({ ids, reason, note, by }) });
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

export async function clearNeedsEdit(ids: string[]): Promise<{ error?: string; cleared?: number }> {
  try {
    return await call("/api/needs-edit/clear", { method: "POST", body: JSON.stringify({ ids }) });
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

export async function fetchInbox(): Promise<Notice[]> {
  try {
    return (await call<{ notices: Notice[] }>("/api/inbox")).notices;
  } catch {
    return [];
  }
}

export async function dismissNotice(id: number): Promise<void> {
  try {
    await call("/api/inbox/dismiss", { method: "POST", body: JSON.stringify({ id }) });
  } catch {
    // The list refreshes either way.
  }
}
